
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import Select from 'react-select';

import { IPublicationViewMessages } from '../../../../../types';
import { EMBED_SYNC_SETTING } from '../../../../constants';
import { styles } from './BulkUpdateReportDataModal.styles';

export interface IBulkUpdateReportDataModalProps {
  show: boolean;
  messages: IPublicationViewMessages;
  selectedReportIds: { embedId: string; storyId: string }[];
  updateDataFrequency(
    selectedReportIds: { storyId: string; embedId: string }[],
    frequencyType?: string
  ): Promise<null | string[]>;
  onClose?(): void;
  setFailedEmbedUpdateIds?(failedEmbeds: string[]): void;
}

export const BulkUpdateReportDataModal: React.FC<IBulkUpdateReportDataModalProps> = (props) => {
  const { messages, selectedReportIds, show, setFailedEmbedUpdateIds, updateDataFrequency } = props;
  const [frequencyType, setFrequencyType] = useState<{ id: string; name: string }>({ id: '', name: '' });

  const onSelectOptionChange = (selected: { id: string; name: string }) => {
    setFrequencyType({ id: selected.id, name: selected.name });
  };

  const onConfirmUpdateReport = async () => {
    const failedEmbeds = await updateDataFrequency(selectedReportIds, frequencyType.id);
    if (failedEmbeds && failedEmbeds.length) {
      setFailedEmbedUpdateIds(failedEmbeds);
    }
    props.onClose();
  };

  if (!show) return null;

  return (
    <Dialog open={true} onClose={props.onClose} fullWidth maxWidth="sm" data-test="bulk-update-report" aria-labelledby="bulk-update-report-dialog-title">
      <DialogTitle id="bulk-update-report-dialog-title" sx={styles.dialogTitle}>
        {messages.updatePublicationDescription}
        <IconButton aria-label="close" onClick={props.onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box>
          <Typography variant="body2" sx={styles.formLabel}>
            {messages.labelDataUpdateFreq}<span aria-hidden={true}> *</span>
          </Typography>
          <Select
            onChange={onSelectOptionChange}
            value={frequencyType}
            clearable={frequencyType.id.length > 0}
            labelKey="name"
            valueKey="id"
            resetValue={{ id: '', name: '' }}
            options={[
              { id: EMBED_SYNC_SETTING.SNAPSHOT, name: messages.useSnapshot },
              { id: EMBED_SYNC_SETTING.REFRESH_ON_UPDATE, name: messages.refreshOnUpdate },
              { id: EMBED_SYNC_SETTING.LIVE, name: messages.liveView }
            ]}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={styles.buttons}>
        <Button variant="outlined" color="secondary" size="small" data-test="cancel-update-button" onClick={props.onClose}>
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          data-test="confirm-update-button"
          onClick={onConfirmUpdateReport}
          disabled={frequencyType.id.length === 0}
        >
          {messages.update}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
